import { existsFilter } from '@iotv/core-services';
import { AdjacentType, Device, DeviceClassName, edgeTypeMatrix, Gateway, GatewayClassName, getChildEdgeTypeId, getParentEdgeTypeId, getZombieEdge, GroupClassName, makePresentable, PathDirection, UserClassName, } from '@iotv/datamodel';
import { v4 } from 'uuid';
import { ConsumerDao } from '../../../../data/ConsumerDAO';
import { UserDAO } from '../../../../data/SystemManagementDAO';
import { Severity, } from '../../../../types';
import { addNewDevice, addNewGateway } from '../../../../v2/components/generic/ObjectTree/standardTransactionVariantFns';
const debug = process.env.REACT_APP_DEBUG && false;
/** Maybe needs a name more like "mutateDetailCardHandledTransactionFns" as that's how it's used. Used in listerlzier and nav-menu context menu to enable object-type-specific actions on save and ALSO to close the new object dialog on save. Passed to the object detail card (which is used as the new object dialog) */
export const mutateListerlizerTransactionFns = (params) => {
    let mutated = { ...params.transactionFunctions };
    /** Mutate save */
    mutated = mutateListerlizerSaveNewObjectTransactionFns({ ...params });
    return mutated;
};
export const mutateListerlizerSaveNewObjectTransactionFns = ({ transactionFunctions, userFunctions, resolvedContextObject, newObject, adjacentFilter, contextUser, contextCustomer, mostProximateCustomer, setNewObject, getConfirmation, }) => {
    /**
     * sets up the adjacent filter to be used in saveAndLinkObject where Group:Group
     * this should be moved so that it is not executed unless Group:Group
     */
    const subGroupAdjacentFilter = {
        adjacentType: AdjacentType.CHILD,
        edgeFilter: {
            edgeTypeId: edgeTypeMatrix.Group.Group.group_has_subGroup.edgeTypeId,
        },
        objectType: resolvedContextObject?.supportedType,
        otherObjectType: undefined,
    };
    let mutatedTransactionFunctions = { ...transactionFunctions };
    switch (newObject?.type) {
        /** This function is given to the UserDetailCard by the listerlizer, but the UserDAO requires more info than usual for its addUserRequest. To get this info here, the UserDetailCard adds the info on to the user object, then it gets used and deleted off the object in this function. */
        case UserClassName: {
            mutatedTransactionFunctions = {
                ...transactionFunctions,
                saveObject: async (objectInCreation) => {
                    if (!contextUser) {
                        console.log('no user');
                        return;
                    }
                    /** Clear up data passed in from UserDetailCard */
                    const makeAdmin = objectInCreation.makeAdmin ? true : false;
                    delete objectInCreation.makeAdmin;
                    const addToGroups = [...objectInCreation.addToGroups];
                    delete objectInCreation.addToGroups;
                    const adminCustomer = { ...objectInCreation.adminCustomer };
                    delete objectInCreation.adminCustomer;
                    /** Make sure we're adding to a user group */
                    if (!resolvedContextObject || resolvedContextObject.type !== 'Group' || resolvedContextObject.supportedType !== 'User') {
                        console.error(`mutateSaveOnTransactionFunctions: resolved context object does not appear to be a user group when saving a user`);
                        return;
                    }
                    const userInCreation = objectInCreation;
                    /** Save User */
                    const userObj = {
                        ...userInCreation,
                        name: userInCreation.name,
                        temporaryPassword: userInCreation.temporaryPassword,
                        username: userInCreation.username ?? '',
                        email: userInCreation.email,
                        phoneNumber: userInCreation.phoneNumber ?? userInCreation.phone_number,
                        creation_date: Date.now(),
                    };
                    const addUserRequest = {
                        makeAdmin,
                        user: userObj,
                        temporaryPassword: objectInCreation.temporaryPassword,
                        contextUser,
                        contextCustomer: adminCustomer,
                        addToGroups,
                    };
                    debug && console.log(`mutateSaveOnTransactionFunctions: new user add request`, addUserRequest);
                    const user_res = await UserDAO.addUser(addUserRequest);
                    debug && console.log('mutateSaveOnTransactionFunctions: new user save res', user_res);
                    if (user_res.err) {
                        let message = `${makePresentable(user_res.err.message)}`;
                        switch (user_res.err.message) {
                            case 'username exists': {
                                message = `A user already exists with that username`;
                                break;
                            }
                        }
                        console.error(`mutateSaveOnTransactionFunctions: error adding new user`, user_res);
                        userFunctions.setUserMessage({
                            id: v4(),
                            label: 'Error creating user',
                            content: message,
                            type: 'MessageBar',
                            severity: Severity.error,
                        });
                        return;
                    }
                    if (!user_res.data) {
                        console.error(`mutateSaveOnTransactionFunctions: no data!?`, user_res);
                        userFunctions.setUserMessage({
                            id: v4(),
                            label: 'Error creating user',
                            content: 'There was an error creating the user',
                            type: 'MessageBar',
                            severity: Severity.error,
                        });
                        return;
                    }
                    const savedUser = user_res.data;
                    // Manually add edge to group... okay... cool...
                    const userGroup = resolvedContextObject;
                    const userGroupEdge = getZombieEdge(userGroup, savedUser, getParentEdgeTypeId('User', 'Group'));
                    const linkToGroup_res = await ConsumerDao.save(userGroupEdge);
                    debug && console.log(`mutateSaveOnTransactionFunctions: group->user edge res`, linkToGroup_res);
                    if (linkToGroup_res.err || !linkToGroup_res.data) {
                        // Remove user ?
                        await UserDAO.deleteUser({ ...savedUser, username: savedUser.username });
                        console.error('mutateSaveOnTransactionFunctions: user saved but error linking. Rolled back addition', linkToGroup_res);
                        userFunctions.setUserMessage({
                            id: v4(),
                            label: 'Error linking user',
                            content: `There was an error adding ${savedUser.name} to ${userGroup.name}`,
                            type: 'MessageBar',
                            severity: Severity.error,
                        });
                        return;
                    }
                    const savedGroup = linkToGroup_res.data;
                    // 2 Feb 2026 - We used to make an edge to the admin customer here, but this is handled in system-provisioning/addUser now.
                    userFunctions.addObjectsToState([savedUser, savedGroup]);
                    userFunctions.setUserMessage({
                        id: v4(),
                        label: 'Successfully created user',
                        content: `Successfully created ${savedUser.name} in ${resolvedContextObject.name}`,
                        type: 'MessageBar',
                        severity: Severity.success,
                    });
                    setNewObject(undefined);
                },
            };
            break;
        }
        case GroupClassName: {
            mutatedTransactionFunctions = {
                ...transactionFunctions,
                saveObject: async (objectInCreation) => {
                    const edgeDefiningObject = resolvedContextObject;
                    if (edgeDefiningObject) {
                        if (edgeDefiningObject.type === GroupClassName) {
                            const parentGroup = edgeDefiningObject;
                            const subGroup = objectInCreation;
                            /** Changes adjacencyFilter by adding supportedType of parent */
                            transactionFunctions.saveAndLinkObject(parentGroup.sk, subGroup, subGroupAdjacentFilter);
                        }
                        else {
                            transactionFunctions.saveAndLinkObject(edgeDefiningObject.sk, objectInCreation, constructGenericFilter({ edgeDefiningObject, objectInCreation, adjacencyType: adjacentFilter.adjacentType }));
                        }
                        userFunctions.addObjectsToState([objectInCreation]);
                        setNewObject(undefined);
                    }
                },
            };
            break;
        }
        case DeviceClassName: {
            mutatedTransactionFunctions = {
                ...transactionFunctions,
                saveObject: async (vob) => {
                    const device = new Device({
                        ...vob,
                        type: DeviceClassName,
                        id: vob.eui,
                        pk: `${DeviceClassName}:${vob.eui}`,
                        sk: `${DeviceClassName}:${vob.eui}`,
                    });
                    if (contextUser && resolvedContextObject && mostProximateCustomer) {
                        let provision = true;
                        let res = { err: null, data: null };
                        if (device.isLNC())
                            provision = await getConfirmation({
                                title: 'Provision on LNC?',
                                question: 'This is an LNC Device, should it also be provisioned on LNC?',
                                positive: 'Save & Provision',
                                alternative: 'Save',
                                neutral: 'Cancel',
                            });
                        if (provision !== null) {
                            if (provision)
                                res = await addNewDevice({
                                    device,
                                    contextUser: contextUser,
                                    deviceGroup: resolvedContextObject,
                                    owningCustomer: mostProximateCustomer,
                                });
                            else
                                res = await ConsumerDao.createAndLink({
                                    existingObject: resolvedContextObject,
                                    newObject: device,
                                    direction: PathDirection.child,
                                });
                            const { err, data } = res;
                            if (err || !data) {
                                console.error(`Error adding Device`, err, data);
                                userFunctions.setUserMessage({
                                    id: v4(),
                                    type: 'MessageBar',
                                    severity: Severity.error,
                                    label: 'Error adding Device',
                                    content: err?.message ?? 'No response',
                                });
                                return;
                            }
                            else {
                                userFunctions.setUserMessage({
                                    id: v4(),
                                    type: 'MessageBar',
                                    severity: Severity.success,
                                    label: 'Added Device',
                                    content: `Successfully added${device.networkKey === 'AWS_LPWAN' ? ' and provisioned' : ''} ${device.name}`,
                                });
                                if (data instanceof Array)
                                    userFunctions.addObjectsToState([data[1], data[2]].filter(existsFilter));
                                else
                                    userFunctions.addObjectsToState([data.device, data.groupEdge]);
                                setNewObject(undefined);
                            }
                        }
                    }
                    else {
                        console.log(`WARN: no user or group or customer`, { contextUser, resolvedContextObject, mostProximateCustomer });
                    }
                },
            };
            break;
        }
        case GatewayClassName: {
            mutatedTransactionFunctions = {
                ...transactionFunctions,
                saveObject: async (vob) => {
                    const gateway = new Gateway({
                        ...vob,
                        type: GatewayClassName,
                        id: vob.eui,
                        pk: `${GatewayClassName}:${vob.eui}`,
                        sk: `${GatewayClassName}:${vob.eui}`,
                    });
                    if (contextUser && resolvedContextObject && mostProximateCustomer) {
                        const { err, data } = await addNewGateway({
                            gateway,
                            contextUser,
                            contextGroup: resolvedContextObject,
                            contextCustomer: mostProximateCustomer,
                        });
                        if (err || !data) {
                            console.error(`Error adding Gateway:`, err, data);
                            userFunctions.setUserMessage({
                                id: v4(),
                                type: 'MessageBar',
                                severity: Severity.error,
                                label: 'Error adding Gateway',
                                content: err?.message ?? 'No response',
                            });
                        }
                        else {
                            userFunctions.setUserMessage({
                                id: v4(),
                                type: 'MessageBar',
                                severity: Severity.success,
                                label: 'Added Gateway',
                                content: `Successfully added${gateway.networkKey === 'AWS_LPWAN' ? ' and provisioned' : ''} ${gateway.name}`,
                            });
                            userFunctions.addObjectsToState([data]);
                            setNewObject(undefined);
                        }
                    }
                    else {
                        console.log(`WARN: no user or group or customer`, { contextUser, resolvedContextObject, mostProximateCustomer });
                    }
                },
            };
            break;
        }
        default: {
            mutatedTransactionFunctions = {
                ...transactionFunctions,
                saveObject: async (vob) => {
                    //see context object not above
                    debug && console.log(`saveObject proxy for saveAndLink has `, { resolvedContextObject });
                    if (resolvedContextObject) {
                        transactionFunctions.saveAndLinkObject(resolvedContextObject.sk, vob, adjacentFilter);
                        setNewObject(undefined);
                        const edgeZombie = { ...vob, pk: resolvedContextObject.sk };
                        debug && console.log('Adding objects to state: ', vob, edgeZombie);
                        const a = userFunctions.addObjectsToState([vob, edgeZombie]);
                        console.log(a);
                    }
                    else {
                        console.log(`WARN: Save and Link has no matched primary!!`);
                    }
                },
            };
        }
    }
    return mutatedTransactionFunctions;
};
export const constructGenericFilter = ({ edgeDefiningObject, objectInCreation, adjacencyType }) => {
    const AdjacentFilter = {
        adjacentType: adjacencyType,
        objectType: objectInCreation.type,
        otherObjectType: edgeDefiningObject.type,
    };
    const edgeTypeId = adjacencyType === AdjacentType.CHILD
        ? getParentEdgeTypeId(objectInCreation.type, edgeDefiningObject.type)
        : getChildEdgeTypeId(objectInCreation.type, edgeDefiningObject.type);
    if (edgeTypeId) {
        AdjacentFilter.edgeFilter = {
            edgeTypeId: edgeTypeId,
        };
    }
    debug && console.log('constructGenericFilter with ', { edgeTypeId, edgeDefiningObject, objectInCreation, adjacencyType }, 'returned ', AdjacentFilter);
    return AdjacentFilter;
};
